import CenterLoader from 'core/components/common/CenterLoader';
import { useLinks } from 'core/components/linksContext';
import { ieFix } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { formatTimeSpan, formatToLocalDayMonth, formatToLocalTime, isEmpty } from 'core/utils';
import React, { useCallback, useState } from 'react';
import Calendar, { CalendarTileProperties, OnChangeDateCallback } from 'react-calendar';
import { GetCourseCalenderDataQuery } from 'site/graphql';
import ExportToGoogleCalendarButton from './ExportToGoogleCalendarButton';
import useAccountCourseCalendar from './hooks/useAccountCourseCalendar';

const borderStyle = '1px solid #eee';

const styles = (theme: ITheme) =>
    createStyles({
        courseCalendarWrap: {
            maxWidth: '100%',
        },
        courseCalendar: {
            position: 'sticky',
            top: 1.5 + 'rem',
            padding: '0.6rem 1.2rem 1.2rem',
            boxShadow: '0 2px 5px 0px rgba(153, 153, 153, 0.48)',
            maxHeight: 'calc(100vh - 5rem)',
            overflow: 'auto',

            ...ieFix({ width: '60%' }),
        },
        title: {
            margin: 0,
            padding: '0 0 0.6rem 0',
            fontSize: '1rem',
            textAlign: 'center',
        },
        calendar: {
            border: 'none !important',
            marginBottom: 1 + 'rem',

            '& .react-calendar__tile': {
                borderRight: borderStyle,
                borderBottom: borderStyle,
                fontSize: 0.8 + 'rem',
                padding: 0.5 + 'rem',

                '&--active': {
                    backgroundColor: theme.colors.accentLight,

                    '&:enabled:hover, &:enabled:focus': {
                        backgroundColor: theme.colors.accentLight,
                    },
                },
            },
            '& .react-calendar__month-view': {
                borderLeft: borderStyle,

                '&__weekdays__weekday': {
                    borderRight: borderStyle,
                    borderBottom: borderStyle,
                },
            },

            '& .react-calendar__navigation': {
                height: 2 + 'rem',
                marginBottom: 0,
                border: borderStyle,

                '&__next2-button, &__prev2-button': {
                    display: 'none',
                },

                '&__label': {
                    fontSize: 0.9 + 'rem',
                },
            },

            [theme.breakpoints.up('md')]: {
                marginBottom: 0.5 + 'rem',
            },
        },
        preview: {
            fontSize: 0.9 + 'rem',
            lineHeight: 1.2,

            '& > *': {
                marginBottom: 0.5 + 'rem',
            },
            '& :last-child': {
                marginBottom: 0,
            },
        },
        preview__date: {
            color: theme.colors.accent,
            fontWeight: 700,
        },
        previewItem: {
            marginBottom: 1 + 'rem',
            '& > *': {
                marginBottom: 0.5 + 'rem',
            },
            '& :last-child': {
                marginBottom: 0,
            },
        },
        previewItem__link: {
            color: theme.colors.accent,
            fontWeight: 700,
        },
        abbr: {
            display: 'inline',
            '&[title]': {
                borderBottom: '1px dotted',
                textDecoration: 'none',
                cursor: 'help',
            },
        },
        eventDay: {
            background: '#FFBFBF',
            'button&': {
                background: '#FFBFBF',
            },
        },
    });

const CourseCalendar: React.FC<WithStyles<typeof styles>> = ({ classes }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [orders, loading, onViewChange] = useAccountCourseCalendar();
    const links = useLinks();

    const tileClassName = useCallback(
        ({ date, view }: CalendarTileProperties) => {
            let isShowBg = false;

            if (view === 'month') {
                isShowBg = orders.some(d => new Date(d.courseDate).toLocaleDateString() === date.toLocaleDateString());
            } else if (view === 'year') {
                isShowBg = orders.some(d => {
                    const valueDate = new Date(d.courseDate);
                    return valueDate.getMonth() === date.getMonth() && valueDate.getFullYear() === date.getFullYear();
                });
            } else if (view === 'decade') {
                isShowBg = orders.some(d => new Date(d.courseDate).getFullYear() === date.getFullYear());
            }

            return isShowBg ? classes.eventDay : null;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orders],
    );

    const renderEventInfo = useCallback(
        (value: GetCourseCalenderDataQuery['extendedOrders']['result'][0]) => {
            const date = new Date(value.courseDate);
            const isPurchased = value.state === 'PURCHASED';

            return (
                <div key={value.id} className={classes.previewItem}>
                    <p>
                        <span className={classes.preview__date}>{formatToLocalTime(date)} </span>
                        &ndash; {value.productName}
                    </p>
                    <p>{value.courseTitle}</p>
                    <p>Продолжительность: {formatTimeSpan(value.courseHours)}</p>
                    <p>
                        <links.productDetail.Link
                            className={classes.previewItem__link}
                            to={
                                isPurchased
                                    ? links.accountProductDetail.url(value.productId)
                                    : links.productDetail.url(value.productId)
                            }
                        >
                            Подробнее
                        </links.productDetail.Link>
                        {isPurchased ? '' : ' (не оплачено)'}
                    </p>
                </div>
            );
        },
        [classes, links],
    );

    const currentDateDays = orders.filter(
        d => new Date(d.courseDate).toLocaleDateString() === currentDate.toLocaleDateString(),
    );
    const haveDays = !isEmpty(currentDateDays);

    return (
        <div className={classes.courseCalendarWrap}>
            <div className={classes.courseCalendar}>
                {loading && <CenterLoader absolute />}
                <h3 className={classes.title}>Календарь</h3>
                <Calendar
                    className={classes.calendar}
                    locale="ru-RU"
                    calendarType="ISO 8601"
                    onChange={setCurrentDate as OnChangeDateCallback}
                    value={currentDate}
                    onActiveDateChange={onViewChange} // клик по ">" или "<"
                    onDrillDown={onViewChange} // переход по временым кварталам вниз
                    onDrillUp={onViewChange} // переход по временым кварталам вверх
                    tileClassName={tileClassName}
                />

                <div className={classes.preview}>
                    <p>
                        На <span className={classes.preview__date}>{formatToLocalDayMonth(currentDate)}</span>{' '}
                        {haveDays ? 'у вас запланировано:' : 'у вас ничего не запланировано'}
                    </p>

                    {haveDays && currentDateDays.map(cdd => renderEventInfo(cdd))}
                </div>

                <ExportToGoogleCalendarButton />
            </div>
        </div>
    );
};

export default injectSheet(styles, 'CourseCalendar')(CourseCalendar);
